/**
 * The day's inbound calls, bucketed by Pacific hour.
 *
 * Rows come off `report.hours[].calls`, the same classification the hour headers
 * are counted from, so a header can never disagree with the rows beneath it.
 * Recordings play through the shared `CallRecordingPlayer` — the same one the
 * patient timeline uses.
 */

import { CallRecordingPlayer } from '@/components/CallRecordingPlayer';
import { formatCallLength } from '@bariendo/lib/call-log';
import {
  PacCallDisposition,
  PacCallLogEntry,
  PacDailyReport,
  PacHourBucket,
  PacIntroCallLogEntry,
  ratioPercent,
} from '@bariendo/lib/pac-daily-report';
import { formatPhoneNumber } from '@bariendo/lib/phone-validations';
import { Anchor, Badge, Button, Group, Paper, Progress, Stack, Text, Title, Tooltip } from '@mantine/core';
import { IconVideo } from '@tabler/icons-react';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { ReactElement, useState } from 'react';
import { PAC_REPORT_TIMEZONE } from './usePacDailyReport';

dayjs.extend(utc);
dayjs.extend(timezone);

/** How many rows an hour shows before collapsing behind "Show all N". */
const COLLAPSE_THRESHOLD = 5;

export type CallLogFilter = 'all' | PacCallDisposition;

export interface CallLogProps {
  report: PacDailyReport;
  /** `Patient/<id>` → display name, for callers whose number matched a patient. */
  patientNames: Record<string, string>;
  /** `Practitioner/<id>` → display name, for whoever answered. */
  practitionerNames: Record<string, string>;
}

const DISPOSITION_BADGE: Record<PacCallDisposition, { label: string; color: string }> = {
  'picked-up': { label: 'Picked up', color: 'green' },
  missed: { label: 'Missed', color: 'red' },
  // "Forwarded", not the mockup's "Voicemail": there is no mailbox in the IVR, so
  // what actually happens is a hand-off out of the building — to AnswerConnect
  // for a known patient, to the sales line for an unknown caller. Naming the
  // hand-off rather than one of its two destinations keeps the badge honest.
  forwarded: { label: 'Forwarded', color: 'orange' },
  'in-progress': { label: 'Live', color: 'blue' },
};

/** `4:12`. Seconds are always two digits so a column of durations lines up. */
function formatPacificHour(hour: number): string {
  return dayjs().tz(PAC_REPORT_TIMEZONE).hour(hour).minute(0).format('h:mma');
}

function formatPacificTime(sent: string): string {
  return dayjs(sent).tz(PAC_REPORT_TIMEZONE).format('h:mma');
}

function CallerCell({
  entry,
  patientNames,
}: {
  entry: PacCallLogEntry;
  patientNames: Record<string, string>;
}): ReactElement {
  const name = entry.patientReference ? patientNames[entry.patientReference] : undefined;
  const number = entry.callerNumber ? formatPhoneNumber(entry.callerNumber) : undefined;

  return (
    <Stack gap={0} style={{ minWidth: 160 }}>
      {/* An unknown number has no patient behind it, so the number is the
          identity rather than a subtitle under a name. */}
      <Text size="sm">{name ?? number ?? 'Unknown caller'}</Text>
      {name && number && (
        <Text size="xs" c="dimmed">
          {number}
        </Text>
      )}
    </Stack>
  );
}

function CallRow({
  entry,
  patientNames,
  practitionerNames,
}: {
  entry: PacCallLogEntry;
  patientNames: Record<string, string>;
  practitionerNames: Record<string, string>;
}): ReactElement {
  const isLive = entry.disposition === 'in-progress';
  const badge = DISPOSITION_BADGE[entry.disposition];
  const answeredBy = entry.answeredBy ? (practitionerNames[entry.answeredBy] ?? entry.answeredBy) : undefined;

  return (
    <Stack gap={4} py="xs" style={{ borderTop: '1px solid var(--mantine-color-gray-2)' }}>
      <Group gap="md" wrap="nowrap" align="center">
        <Text size="sm" c="dimmed" style={{ minWidth: 64 }}>
          {formatPacificTime(entry.sent)}
        </Text>

        <CallerCell entry={entry} patientNames={patientNames} />

        <Badge size="sm" variant="light" color={badge.color}>
          {badge.label}
        </Badge>

        {/* The message is what makes a forwarded call actionable, so it earns its
            own marker rather than being buried in the badge. */}
        {entry.messageTaken && (
          <Tooltip label="The answering service sent a message back for this call.">
            <Badge size="sm" variant="outline" color="orange">
              Message taken
            </Badge>
          </Tooltip>
        )}

        <Text size="sm" c={answeredBy ? undefined : 'dimmed'} style={{ minWidth: 140 }}>
          {answeredBy ?? '—'}
        </Text>

        {/* A live call has no duration yet. Rendering the zero Twilio has so far
            reported would say the call lasted no time at all. */}
        <Text size="sm" c={isLive ? 'dimmed' : undefined} style={{ minWidth: 56 }}>
          {isLive ? 'Recording…' : entry.durationSeconds > 0 ? formatCallLength(entry.durationSeconds) : '—'}
        </Text>
      </Group>

      {entry.recordings.length > 0 && !isLive ? (
        <CallRecordingPlayer attachments={entry.recordings} resetKey={entry.reference} />
      ) : (
        !isLive && (
          // Never a play button with nothing behind it.
          <Text size="xs" c="dimmed">
            No recording
          </Text>
        )
      )}
    </Stack>
  );
}

/**
 * Zoom's recordings list. Not the session itself — deep-linking one needs
 * `cloud_recording:read:list_recording_files:admin`, which the marketplace app
 * does not have granted (BAR-2518). The link goes somewhere useful in the
 * meantime rather than nowhere.
 */
const ZOOM_RECORDINGS_PORTAL = 'https://zoom.us/recording/management';

const INTRO_CALL_BADGE: Record<PacIntroCallLogEntry['status'], { label: string; color: string }> = {
  shown: { label: 'Showed', color: 'green' },
  'no-show': { label: 'No-show', color: 'red' },
  booked: { label: 'Booked', color: 'gray' },
};

function IntroCallRow({
  entry,
  patientNames,
  practitionerNames,
}: {
  entry: PacIntroCallLogEntry;
  patientNames: Record<string, string>;
  practitionerNames: Record<string, string>;
}): ReactElement {
  const badge = INTRO_CALL_BADGE[entry.status];
  const patient = entry.patientReference ? patientNames[entry.patientReference] : undefined;
  const coordinator = entry.practitionerReference
    ? (practitionerNames[entry.practitionerReference] ?? entry.practitionerReference)
    : undefined;
  // A no-show has nothing to watch and no minutes to report, so neither is
  // rendered as a zero.
  const isNoShow = entry.status === 'no-show';

  return (
    <Stack gap={4} py="xs" style={{ borderTop: '1px solid var(--mantine-color-gray-2)' }}>
      <Group gap="md" wrap="nowrap" align="center">
        <Text size="sm" c="dimmed" style={{ minWidth: 64 }}>
          {formatPacificTime(entry.start)}
        </Text>

        <Group gap={6} wrap="nowrap" style={{ minWidth: 160 }}>
          {/* The glyph is what separates a booked Zoom consult from a phone call
              at a glance; the two are otherwise the same shape of row. */}
          <Tooltip label="Scheduled intro call (Zoom)">
            <IconVideo size={16} color="var(--mantine-color-blue-6)" />
          </Tooltip>
          {/* A reference we could not resolve is still an identity — showing it
              beats "Unknown patient", which claims we do not know who called. */}
          <Text size="sm">{patient ?? entry.patientReference ?? 'No patient on the appointment'}</Text>
        </Group>

        <Badge size="sm" variant="light" color={badge.color}>
          {badge.label}
        </Badge>

        <Text size="sm" c={coordinator ? undefined : 'dimmed'} style={{ minWidth: 140 }}>
          {coordinator ?? '—'}
        </Text>

        <Text size="sm" c="dimmed" style={{ minWidth: 56 }}>
          {isNoShow || !entry.durationMinutes ? '—' : `${entry.durationMinutes}m`}
        </Text>
      </Group>

      {!isNoShow && (
        <Group gap="xs">
          <Tooltip
            label={
              entry.meetingId
                ? `Opens the Zoom recordings list. Linking straight to meeting ${entry.meetingId} needs the cloud-recording scope (BAR-2518).`
                : 'This appointment has no Zoom join URL, so there is no meeting to look up.'
            }
          >
            <Anchor href={ZOOM_RECORDINGS_PORTAL} target="_blank" rel="noreferrer" size="xs">
              Recordings in Zoom
            </Anchor>
          </Tooltip>
        </Group>
      )}
    </Stack>
  );
}

function HourBlock({
  bucket,
  patientNames,
  practitionerNames,
}: {
  bucket: PacHourBucket;
  patientNames: Record<string, string>;
  practitionerNames: Record<string, string>;
}): ReactElement {
  const [expanded, setExpanded] = useState(false);
  const pickupPercent = ratioPercent(bucket.pickupRate);
  const hidden = bucket.calls.length - COLLAPSE_THRESHOLD;

  const visible = expanded ? bucket.calls : bucket.calls.slice(0, COLLAPSE_THRESHOLD);

  return (
    <Paper withBorder radius="md" p="md">
      <Group justify="space-between" wrap="wrap" gap="xs">
        <Group gap="md" align="baseline">
          <Text fw={600}>{formatPacificHour(bucket.hour)}</Text>
          <Text size="sm" c="dimmed">
            <b>{bucket.inbound} inbound</b> · {bucket.pickedUp} picked up · {bucket.missed} missed · {bucket.forwarded}{' '}
            forwarded
            {bucket.inProgress > 0 ? ` · ${bucket.inProgress} live` : ''}
          </Text>
          {/* Counted separately, never folded into inbound: a booked Zoom consult
              was never offered to the phone queue, so it cannot be missed. */}
          {bucket.scheduledIntroCalls > 0 && (
            <Text size="sm" c="dimmed">
              {bucket.scheduledIntroCalls} scheduled intro {bucket.scheduledIntroCalls === 1 ? 'call' : 'calls'} ·{' '}
              {bucket.scheduledIntroCallsShown} showed
            </Text>
          )}
        </Group>
        <Group gap="xs" align="center" style={{ minWidth: 140 }}>
          <Text size="sm" c="dimmed">
            {pickupPercent === undefined ? '—' : `${pickupPercent}%`}
          </Text>
          <Progress value={pickupPercent ?? 0} size="sm" style={{ flex: 1 }} aria-label="Pickup rate" />
        </Group>
      </Group>

      {bucket.introCalls.map((entry) => (
        <IntroCallRow
          key={entry.reference}
          entry={entry}
          patientNames={patientNames}
          practitionerNames={practitionerNames}
        />
      ))}

      {visible.map((entry) => (
        <CallRow
          key={entry.reference}
          entry={entry}
          patientNames={patientNames}
          practitionerNames={practitionerNames}
        />
      ))}

      {hidden > 0 && (
        <Button variant="subtle" size="xs" mt="xs" onClick={() => setExpanded((open) => !open)}>
          {expanded ? 'Show fewer' : `Show all ${bucket.calls.length} calls in this hour`}
        </Button>
      )}
    </Paper>
  );
}

export function CallLog({ report, patientNames, practitionerNames }: CallLogProps): ReactElement {
  const [filter, setFilter] = useState<CallLogFilter>('all');

  const hours = report.hours
    .map((bucket) => ({
      ...bucket,
      calls: filter === 'all' ? bucket.calls : bucket.calls.filter((entry) => entry.disposition === filter),
      // A scheduled Zoom consult has no disposition to filter on, so the chips
      // are about phone calls only and the intro rows step aside while one is on.
      introCalls: filter === 'all' ? bucket.introCalls : [],
    }))
    // Hours with nothing in them are omitted rather than rendered empty, and a
    // filter that empties an hour hides it for the same reason.
    .filter((bucket) => bucket.calls.length > 0 || bucket.introCalls.length > 0);

  return (
    <Stack gap="xs">
      <Group justify="space-between" align="center" wrap="wrap">
        <Group gap="xs" align="baseline">
          <Title order={2} size="h4">
            Call log
          </Title>
          <Text size="sm" c="dimmed">
            {report.inboundCalls.total} inbound · bucketed by hour (PT)
          </Text>
        </Group>

        <Group gap={4}>
          {/* The mockup's fourth chip is "Voicemail". Nothing in this system takes
              a voicemail — an unanswered call is dialled out to the answering
              service or the sales line — so the chip filters on that hand-off
              instead, under a name that covers both. */}
          {(['all', 'picked-up', 'missed', 'forwarded'] as const).map((option) => (
            <Button
              key={option}
              size="xs"
              variant={filter === option ? 'filled' : 'default'}
              onClick={() => setFilter(option)}
            >
              {option === 'all' ? 'All' : DISPOSITION_BADGE[option].label}
            </Button>
          ))}
        </Group>
      </Group>

      {hours.length === 0 ? (
        <Paper withBorder radius="md" p="md">
          <Text size="sm" c="dimmed">
            {report.inboundCalls.total === 0 ? 'No inbound calls on this day.' : 'No calls match this filter.'}
          </Text>
        </Paper>
      ) : (
        hours.map((bucket) => (
          <HourBlock
            key={bucket.hour}
            bucket={bucket}
            patientNames={patientNames}
            practitionerNames={practitionerNames}
          />
        ))
      )}
    </Stack>
  );
}
